/**
 * Character Sanitization Utility for Thermal Printers
 * Handles conversion of Unicode characters to printer-safe ASCII equivalents
 * 
 * Addresses the issue where Hindi/Marathi text and special symbols
 * appear as Chinese characters or garbage on thermal printers
 * 
 * @author QuickBill Team
 * @version 1.0.0
 */

/**
 * Common character replacements for thermal printer compatibility
 */
const CHARACTER_REPLACEMENTS = {
  // Currency symbols
  '₹': 'Rs.',
  '₨': 'Rs.',
  '$': 'USD ',
  '€': 'EUR ',
  '£': 'GBP ',
  '¥': 'JPY ',
  
  // Hindi characters to Roman equivalents
  'अ': 'a', 'आ': 'aa', 'इ': 'i', 'ई': 'ii', 'उ': 'u', 'ऊ': 'uu',
  'ए': 'e', 'ऐ': 'ai', 'ओ': 'o', 'औ': 'au',
  'क': 'ka', 'ख': 'kha', 'ग': 'ga', 'घ': 'gha', 'ङ': 'nga',
  'च': 'cha', 'छ': 'chha', 'ज': 'ja', 'झ': 'jha', 'ञ': 'nja',
  'ट': 'ta', 'ठ': 'tha', 'ड': 'da', 'ढ': 'dha', 'ण': 'na',
  'त': 'ta', 'थ': 'tha', 'द': 'da', 'ध': 'dha', 'न': 'na',
  'प': 'pa', 'फ': 'pha', 'ब': 'ba', 'भ': 'bha', 'म': 'ma',
  'य': 'ya', 'र': 'ra', 'ल': 'la', 'व': 'va',
  'श': 'sha', 'ष': 'sha', 'स': 'sa', 'ह': 'ha',
  
  // Marathi specific characters
  'ळ': 'la', 'क्ष': 'ksha', 'ज्ञ': 'gya',
  
  // Common Hindi/Marathi words
  'चाय': 'chai', 'पत्ती': 'patti', 'दूध': 'dudh', 'चीनी': 'cheeni',
  'नमक': 'namak', 'तेल': 'tel', 'घी': 'ghee', 'दाल': 'dal',
  'चावल': 'chawal', 'आटा': 'aata', 'सब्जी': 'sabji',
  
  // Special characters and symbols
  '™': '(TM)', '®': '(R)', '©': '(C)', '°': 'deg',
  '±': '+/-', '×': 'x', '÷': '/', '≤': '<=', '≥': '>=',
  '\u2018': "'", '\u2019': "'", '\u201C': '"', '\u201D': '"',
  '–': '-', '—': '-', '…': '...', '•': '*',
  
  // Mathematical and technical symbols  
  '√': 'sqrt', '∞': 'inf', 'π': 'pi', 'Σ': 'sum',
  'Δ': 'delta', 'Ω': 'omega', 'μ': 'micro', 'α': 'alpha',
  'β': 'beta', 'γ': 'gamma', 'θ': 'theta', 'λ': 'lambda',
  
  // Fractions
  '½': '1/2', '⅓': '1/3', '¼': '1/4', '¾': '3/4',
  '⅕': '1/5', '⅖': '2/5', '⅗': '3/5', '⅘': '4/5',
  '⅙': '1/6', '⅚': '5/6', '⅛': '1/8', '⅜': '3/8',
  '⅝': '5/8', '⅞': '7/8',
}

/**
 * Word-level replacements for better context
 */
const WORD_REPLACEMENTS = {
  // Hindi/Marathi product names
  'चाय पत्ती': 'Chai Patti',
  'दूध पाउडर': 'Milk Powder',
  'चीनी': 'Sugar',
  'नमक': 'Salt',
  'तेल': 'Oil',
  'घी': 'Ghee',
  'आटा': 'Wheat Flour',
  'चावल': 'Rice',
  'दाल': 'Lentils',
  'सब्जी': 'Vegetables',
  
  // Marathi specific
  'साखर': 'Sugar',
  'मीठ': 'Salt',
  'तूप': 'Ghee',
  'कांदा': 'Onion',
  'बटाटा': 'Potato',
}

/**
 * Configuration for character sanitization
 */
export const SANITIZER_CONFIG = {
  // Enable/disable sanitization
  enabled: true,
  
  // Replacement strategy
  strategy: 'transliterate', // 'transliterate', 'remove', 'replace'
  
  // Fallback character for unknown characters
  fallbackChar: '?',
  
  // Whether to preserve ASCII characters
  preserveAscii: true,
  
  // Whether to use word-level replacements
  useWordReplacements: true,
  
  // Custom replacements (can be extended by users)
  customReplacements: {}
}

/**
 * Character Sanitizer Class
 */
export class CharacterSanitizer {
  constructor(config = {}) {
    this.config = { ...SANITIZER_CONFIG, ...config }
    this.characterMap = { ...CHARACTER_REPLACEMENTS, ...this.config.customReplacements }
    this.wordMap = { ...WORD_REPLACEMENTS }
  }

  /**
   * Sanitize text for thermal printer compatibility
   */
  sanitize(text) {
    if (!this.config.enabled || !text) {
      return text
    }

    let sanitized = text.toString()

    // First apply word-level replacements for better context
    if (this.config.useWordReplacements) {
      for (const [original, replacement] of Object.entries(this.wordMap)) {
        const regex = new RegExp(original, 'g')
        sanitized = sanitized.replace(regex, replacement)
      }
    }

    // Then apply character-level replacements
    for (const [original, replacement] of Object.entries(this.characterMap)) {
      const regex = new RegExp(this.escapeRegExp(original), 'g')
      sanitized = sanitized.replace(regex, replacement)
    }

    // Handle remaining non-ASCII characters based on strategy
    switch (this.config.strategy) {
      case 'transliterate':
        sanitized = this.transliterateRemaining(sanitized)
        break
      case 'remove':
        sanitized = this.removeNonAscii(sanitized)
        break
      case 'replace':
        sanitized = this.replaceNonAscii(sanitized)
        break
    }

    return sanitized
  }

  /**
   * Transliterate remaining non-ASCII characters
   */
  transliterateRemaining(text) {
    return text.replace(/[^\x00-\x7F]/g, (match) => {
      // Try to find a close ASCII equivalent
      const charCode = match.charCodeAt(0)
      
      // Handle accented characters
      const normalized = match.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      if (normalized !== match && /^[\x00-\x7F]$/.test(normalized)) {
        return normalized
      }
      
      // Fallback to replacement character
      return this.config.fallbackChar
    })
  }

  /**
   * Remove non-ASCII characters
   */
  removeNonAscii(text) {
    return text.replace(/[^\x00-\x7F]/g, '')
  }

  /**
   * Replace non-ASCII characters with fallback
   */
  replaceNonAscii(text) {
    return text.replace(/[^\x00-\x7F]/g, this.config.fallbackChar)
  }

  /**
   * Escape special regex characters
   */
  escapeRegExp(string) {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  }

  /**
   * Check if text contains non-ASCII characters
   */
  hasNonAsciiChars(text) {
    return /[^\x00-\x7F]/.test(text)
  }

  /**
   * Get sanitization preview (for debugging)
   */
  getPreview(text) {
    const original = text
    const sanitized = this.sanitize(text)
    const hasChanges = original !== sanitized
    
    return {
      original,
      sanitized,
      hasChanges,
      nonAsciiCount: (original.match(/[^\x00-\x7F]/g) || []).length
    }
  }

  /**
   * Add custom character replacement
   */
  addReplacement(original, replacement) {
    this.characterMap[original] = replacement
  }

  /**
   * Add custom word replacement
   */
  addWordReplacement(original, replacement) {
    this.wordMap[original] = replacement
  }

  /**
   * Update configuration
   */
  updateConfig(newConfig) {
    this.config = { ...this.config, ...newConfig }
  }
}

/**
 * Default sanitizer instance
 */
export const defaultSanitizer = new CharacterSanitizer()

/**
 * Quick sanitization function
 */
export function sanitizeText(text, config = {}) {
  if (!config.enabled && Object.keys(config).length === 1) {
    return text // Skip sanitization if explicitly disabled
  }
  
  const sanitizer = new CharacterSanitizer(config)
  return sanitizer.sanitize(text)
}

/**
 * Sanitize currency amount
 */
export function sanitizeCurrency(amount, config = {}) {
  const formatted = `Rs.${parseFloat(amount).toFixed(2)}`
  return sanitizeText(formatted, config)
}

/**
 * Test function to check sanitization results
 */
export function testSanitization() {
  const testCases = [
    'चाय पत्ती Premium',
    '₹1,234.56',
    'राम शर्मा',
    'Laptop Computer',
    'Special™ Product®',
    'Temperature: 25°C',
    'Quantity: ½ kg'
  ]

  console.log('Character Sanitization Test Results:')
  console.log('=====================================')

  testCases.forEach(test => {
    const result = defaultSanitizer.getPreview(test)
    console.log(`Original: "${result.original}"`)
    console.log(`Sanitized: "${result.sanitized}"`)
    console.log(`Changed: ${result.hasChanges}`)
    console.log(`Non-ASCII chars: ${result.nonAsciiCount}`)
    console.log('---')
  })
}

// Export everything
export default CharacterSanitizer